<script setup lang="ts">
import type { FormInstance, FormRules } from 'element-plus'
import type { IRegisterResponse } from '@/entities/auth'
import type { IEmailOtpVerifyResponse } from '@/entities/emailOtp'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useCheckEmailMutation, useRegisterMutation } from '@/entities/auth'
import {
  useEmailOtpSendMutation,
  useEmailOtpVerifyMutation,
} from '@/entities/emailOtp'
import { setAuthTokens, useCustomToast } from '@/shared/lib'
import CDialog from '@/shared/ui/CDialog.vue'

enum Steps {
  Register = 1,
  Confirmation = 2,
}

defineEmits<Emits>()

const activeStep = ref(Steps.Register)

interface Emits {
  (e: 'showLoginDialog'): void
}
const { t } = useI18n()
const { error: showError, success } = useCustomToast()
const router = useRouter()

const model = defineModel({ default: true })

const TITLES = {
  [Steps.Register]: 'register',
  [Steps.Confirmation]: 'enter_email_code',
}

const stepTitle = computed(() => {
  return TITLES[activeStep.value]
})

// Form refs
const registerFormRef = ref<FormInstance>()
const smsFormRef = ref<FormInstance>()

// Form data
const form = ref({
  first_name: '',
  last_name: '',
  email: '',
  phone_number: '',
  password: '',
  confirmPassword: '',
})

const smsForm = ref({
  smsCode: '',
})

// Validation rules
const registerRules: FormRules = {
  first_name: [
    { required: true, message: t('name_required'), trigger: 'blur' },
    { min: 1, max: 150, message: t('name_length_error'), trigger: 'blur' },
  ],
  last_name: [
    { required: true, message: t('lastname_required'), trigger: 'blur' },
    { min: 1, max: 150, message: t('lastname_length_error'), trigger: 'blur' },
  ],
  email: [
    { required: true, message: t('email_required'), trigger: 'blur' },
    { type: 'email', message: t('invalid_email_format'), trigger: 'blur' },
    { max: 254, message: t('email_length_error'), trigger: 'blur' },
  ],
  phone_number: [
    { required: true, message: t('phone_required'), trigger: 'blur' },
    { min: 1, max: 15, message: t('phone_length_error'), trigger: 'blur' },
  ],
  password: [
    { required: true, message: t('password_required'), trigger: 'blur' },
    { min: 1, message: t('password_min_length'), trigger: 'blur' },
  ],
  confirmPassword: [
    {
      required: true,
      message: t('password_confirm_required'),
      trigger: 'blur',
    },
    {
      validator: (rule, value, callback) => {
        if (value !== form.value.password) {
          callback(new Error(t('passwords_not_match')))
        }
        else {
          callback()
        }
      },
      trigger: 'blur',
    },
  ],
}

const smsRules: FormRules = {
  smsCode: [
    { required: true, message: t('email_code_required'), trigger: 'blur' },
  ],
}

// Loading state
const isSubmitting = ref(false)

// API mutations
const { mutate: checkEmail } = useCheckEmailMutation()
const { mutate: sendEmailOtp } = useEmailOtpSendMutation()
const { mutate: verifyEmailOtp } = useEmailOtpVerifyMutation()
const { mutate: register } = useRegisterMutation()

function handleBack() {
  if (activeStep.value === Steps.Confirmation) {
    activeStep.value = Steps.Register
  }
}

function onRegister() {
  if (activeStep.value === Steps.Register) {
    handleGetCode()
  }
  else {
    handleConfirmRegistration()
  }
}

async function handleGetCode() {
  if (!registerFormRef.value) 
    return

  try {
    await registerFormRef.value.validate()
    isSubmitting.value = true

    // First check if email exists
    checkEmail(form.value.email, {
      onSuccess: (response) => {
        // Check if email exists in response
        if (response.exists) {
          // Email already exists
          showError({
            title: t('registration_failed'),
            message: t('email_already_exists'),
          })
          isSubmitting.value = false
        }
        else {
          // Email is new, send OTP
          sendEmailOtp(
            { email: form.value.email },
            {
              onSuccess: () => {
                activeStep.value = Steps.Confirmation
                success({
                  title: t('code_sent_successfully'),
                  message: t('code_sent_successfully_message'),
                })
                isSubmitting.value = false
              },
              onError: (error) => {
                console.error('Failed to send OTP:', error)
                showError({
                  title: t('failed_to_send_code'),
                  message: t('failed_to_send_code_message'),
                })
                isSubmitting.value = false
              },
            },
          )
        }
      },
      onError: (error) => {
        console.error('Email check failed:', error)
        showError({
          title: t('email_check_failed'),
          message: t('email_check_failed_message'),
        })
        isSubmitting.value = false
      },
    })
  }
  catch (error) {
    console.error('Form validation failed:', error)
    isSubmitting.value = false
  }
}

async function handleConfirmRegistration() {
  if (!smsFormRef.value) 
    return

  try {
    await smsFormRef.value.validate()
    isSubmitting.value = true

    // First verify the OTP
    verifyEmailOtp(
      {
        email: form.value.email,
        otp: smsForm.value.smsCode,
      },
      {
        onSuccess: (otpResponse: IEmailOtpVerifyResponse) => {
          // Now register with the OTP ID
          const registerData = {
            first_name: form.value.first_name,
            last_name: form.value.last_name,
            email: form.value.email,
            phone_number: form.value.phone_number,
            password: form.value.password,
            otp: otpResponse.otp, // Use the OTP ID from verification response
          }

          register(registerData, {
            onSuccess: (data: IRegisterResponse) => {
              // Set auth tokens from the tokens object
              setAuthTokens(data.tokens.access, data.tokens.refresh)

              // Show success message
              success({
                title: t('registration_successful'),
                message: t('registration_successful_message'),
              })

              // Close dialog and redirect to dashboard
              model.value = false
              router.push({ name: 'Dashboard' })
            },
            onError: (error: unknown) => {
              console.error('Registration failed:', error)
              showError({
                title: t('registration_failed'),
                message: t('registration_failed_message'),
              })
            },
          })
        },
        onError: (error: unknown) => {
          console.error('OTP verification failed:', error)
          showError({
            title: t('otp_verification_failed'),
            message: t('otp_verification_failed_message'),
          })
        },
      },
    )
  }
  catch (error) {
    console.error('SMS form validation failed:', error)
  }
  finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <CDialog
    v-model="model"
    :title="$t('register')"
    class="sm:!w-[480px] !w-[90%]"
  >
    <template #header>
      <h4 class="flex items-center gap-2">
        <ElButton
          v-if="activeStep === Steps.Confirmation"
          link
          @click="handleBack"
        >
          <ElIcon><ElIconArrowLeft /></ElIcon>
        </ElButton>
        <Transition name="fade" mode="out-in">
          <span :key="activeStep">{{ $t(stepTitle) }}</span>
        </Transition>
      </h4>
    </template>

    <Transition name="fade" mode="out-in">
      <ElForm
        v-if="activeStep === Steps.Register"
        ref="registerFormRef"
        :model="form"
        :rules="registerRules"
        label-position="top"
      >
        <ElFormItem prop="first_name" :label="$t('name')">
          <ElInput v-model="form.first_name" :placeholder="$t('name')" />
        </ElFormItem>

        <ElFormItem prop="last_name" :label="$t('lastname')">
          <ElInput
            v-model="form.last_name"
            :placeholder="$t('enter_lastname')"
          />
        </ElFormItem>

        <ElFormItem prop="email" :label="$t('email')">
          <ElInput v-model="form.email" :placeholder="$t('enter_email')" />
        </ElFormItem>

        <ElFormItem prop="phone_number" :label="$t('phone_number')">
          <ElInput
            v-model="form.phone_number"
            :placeholder="$t('enter_phone_number')"
          />
        </ElFormItem>

        <ElFormItem prop="password" :label="$t('password')">
          <ElInput
            v-model="form.password"
            type="password"
            :placeholder="$t('enter_password')"
          />
        </ElFormItem>

        <ElFormItem prop="confirmPassword" :label="$t('password_confirm')">
          <ElInput
            v-model="form.confirmPassword"
            type="password"
            :placeholder="$t('enter_password_confirm')"
          />
        </ElFormItem>
      </ElForm>

      <!-- SMS Code Form -->
      <ElForm
        v-else-if="activeStep === Steps.Confirmation"
        ref="smsFormRef"
        :model="smsForm"
        :rules="smsRules"
        label-position="top"
      >
        <ElFormItem prop="smsCode" :label="$t('enter_code')">
          <ElInput v-model="smsForm.smsCode" :placeholder="$t('enter')" />
        </ElFormItem>
      </ElForm>
    </Transition>

    <template #footer>
      <div class="flex flex-col gap-3">
        <div class="flex items-center justify-between">
          <p class="flex items-center gap-2">
            <ElText>{{ $t("no_account") }}</ElText>
            <ElLink type="primary" @click="$emit('showLoginDialog')">
              {{ $t("login") }}
            </ElLink>
          </p>
          <ElButton type="primary" :loading="isSubmitting" @click="onRegister">
            {{ activeStep === Steps.Register ? $t("get_code") : $t("register") }}
          </ElButton>
        </div>

        <I18nT
          v-if="activeStep === Steps.Register"
          keypath="register_consent_notice"
          tag="p"
          class="text-xs leading-snug text-gray-400"
        >
          <template #consent>
            <RouterLink
              :to="{ name: 'StaticPage', params: { slug: 'consent' } }"
              target="_blank"
              class="underline underline-offset-2 hover:text-brand-primary transition-colors"
            >
              {{ $t('consent_policy') }}
            </RouterLink>
          </template>
          <template #privacy>
            <RouterLink
              :to="{ name: 'StaticPage', params: { slug: 'privacy' } }"
              target="_blank"
              class="underline underline-offset-2 hover:text-brand-primary transition-colors"
            >
              {{ $t('privacy_policy') }}
            </RouterLink>
          </template>
          <template #offer>
            <RouterLink
              :to="{ name: 'StaticPage', params: { slug: 'terms-of-use' } }"
              target="_blank"
              class="underline underline-offset-2 hover:text-brand-primary transition-colors"
            >
              {{ $t('terms_of_use') }}
            </RouterLink>
          </template>
        </I18nT>
      </div>
    </template>
  </CDialog>
</template>
